import React from 'react';

type IconProps = React.HTMLAttributes<HTMLSpanElement> & {
  size?: number;
  strokeWidth?: number;
  fill?: string;
};

function ms(name: string) {
  return function Icon({
    className = '',
    size,
    strokeWidth: _sw,
    fill: _fill,
    style,
    ...rest
  }: IconProps) {
    const hMatch = className.match(/\bh-([\d.]+)\b/);
    const fs = size ?? (hMatch ? parseFloat(hMatch[1]) * 4 : 24);
    const cls = className.replace(/\b[hw]-([\d.]+)\b\s*/g, '').trim();
    return (
      <span
        {...rest}
        className={`material-symbols-sharp select-none ${cls}`}
        style={{
          fontSize: fs,
          lineHeight: 1,
          fontVariationSettings: `'FILL' 0, 'wght' 100, 'GRAD' -50, 'opsz' 24`,
          ...style,
        }}
        aria-hidden="true"
      >
        {name}
      </span>
    );
  };
}

export const ArrowLeft = ms('arrow_back');
export const ArrowRight = ms('arrow_forward');
export const ArrowUpRight = ms('north_east');
export const Bell = ms('notifications');
export const Box = ms('view_in_ar');
export const Camera = ms('photo_camera');
export const Check = ms('check');
export const CheckCircle2 = ms('check_circle');
export const ChevronDown = ms('expand_more');
export const ChevronLeft = ms('chevron_left');
export const ChevronRight = ms('chevron_right');
export const ChevronUp = ms('expand_less');
export const ChevronsRight = ms('keyboard_double_arrow_right');
export const CircleAlert = ms('error');
export const AlertCircle = ms('error');
export const CircleEllipsis = ms('pending');
export const CircleX = ms('cancel');
export const XCircle = ms('cancel');
export const ClipboardCheck = ms('assignment_turned_in');
export const Clock = ms('schedule');
export const Copy = ms('content_copy');
export const CopyIcon = ms('content_copy');
export const Crown = ms('workspace_premium');
export const Download = ms('download');
export const DownloadIcon = ms('download');
export const FileText = ms('description');
export const FileX = ms('block');
export const Flip = ms('flip');
export const FolderSupervised = ms('folder_supervised');
export const Person = ms('person');
export const PersonAdd = ms('person_add');
export const People = ms('group');
export const Groups = ms('groups');
export const Folder = ms('folder');
export const FolderInput = ms('drive_folder_upload');
export const FolderOpen = ms('folder_open');
export const FolderPlus = ms('create_new_folder');
export const Frown = ms('sentiment_dissatisfied');
export const Gem = ms('diamond');
export const Hammer = ms('gavel');
export const Hash = ms('tag');
export const HeartCrack = ms('heart_broken');
export const Location = ms('location_on');
export const Minimize = ms('remove');
export const ArrowUp = ms('arrow_upward');
export const Footprints = ms('directions_run');
export const UserPlus = ms('person_add');
export const History = ms('history');
export const ImageIcon = ms('image');
export const ImagePlus = ms('add_photo_alternate');
export const Images = ms('photo_library');
export const Layers = ms('layers');
export const List = ms('format_list_bulleted');
export const Loader2 = ms('progress_activity');
export const LockKeyhole = ms('lock');
export const LogIn = ms('login');
export const LogOut = ms('logout');
export const Mail = ms('mail');
export const Menu = ms('menu');
export const MessageSquare = ms('chat_bubble');
export const MoreVertical = ms('more_vert');
export const Pencil = ms('edit');
export const Plus = ms('add');
export const PlusIcon = ms('add');
export const Recycle = ms('recycling');
export const RefreshCcw = ms('sync');
export const RefreshCw = ms('refresh');
export const RotateCcw = ms('undo');
export const RotateCw = ms('redo');
export const Rotation3D = ms('3d_rotation');

// Icosahedron icon — replaces the previous Noun-Project SVG. Lives as a
// PNG in /public/icosahedron.png because the user supplied a raster
// asset (no need to hand-author an SVG path from a bitmap when the
// existing usages already accept className-based sizing).
//
// Same API as the svgIcon helpers (className, size): we extract the
// h-N tailwind class to set both the wrapper and the <img> dimensions,
// and apply sizeScale * 1.755 so the visual footprint matches the
// previous Noun-Project icon. The image is currentColor-friendly via
// CSS filter only if the source is already monochrome; if the user's
// icon needs color tinting later, swap to an <object> or inline SVG.
// `tint` lets callers override the rendered color of the PNG. The source
// ships as black-on-transparent so `text-*` Tailwind classes don't affect
// it (CSS `color` does nothing to raster pixels). `tint="white"` applies a
// CSS filter to recolor the bitmap — required on dark backgrounds like the
// unrendered-state file cards, where black-on-dark is invisible. The fix
// twice missed (claiming `text-telekinesis-neutral-500` would work) because
// the previous SVG-based icon honored `color` but the PNG doesn't.
// `colorize` renders the PNG as a CSS mask filled with `currentColor`, so the
// caller's `text-*` class actually tints it (the plain <img> can only be black
// or, via `tint="white"`, white). Used where the icon must match a specific
// label colour (e.g. the parametric chat object icon → model-label colour).
type IcosahedronProps = IconProps & { tint?: 'white'; colorize?: boolean };

const ICON_SIZE_SCALE = 1.755;
export function Icosahedron({
  className = '',
  size,
  style,
  tint,
  colorize,
  ...rest
}: IcosahedronProps) {
  const hMatch = className.match(/\bh-([\d.]+)\b/);
  const baseSize = size ?? (hMatch ? parseFloat(hMatch[1]) * 4 : 24);
  const fs = baseSize * ICON_SIZE_SCALE;
  const cls = className.replace(/\b[hw]-([\d.]+)\b\s*/g, '').trim();
  // brightness(0) flattens every pixel to black; invert(1) then flips the
  // whole bitmap, yielding pure white on the originally-opaque pixels.
  // Transparent pixels stay transparent — the silhouette is preserved.
  const imgFilter = tint === 'white' ? 'brightness(0) invert(1)' : undefined;
  return (
    <span
      {...rest}
      className={`inline-flex shrink-0 select-none items-center justify-center ${cls}`}
      style={{
        width: fs,
        height: fs,
        lineHeight: 0,
        ...style,
      }}
      aria-hidden="true"
    >
      {colorize ? (
        // Mask the silhouette and fill with currentColor so `text-*` tints it.
        <span
          style={{
            width: fs,
            height: fs,
            display: 'block',
            backgroundColor: 'currentColor',
            WebkitMaskImage: "url('/icosahedron.png')",
            maskImage: "url('/icosahedron.png')",
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
          }}
        />
      ) : (
        <img
          src="/icosahedron.png"
          alt=""
          width={fs}
          height={fs}
          draggable={false}
          style={{
            width: fs,
            height: fs,
            objectFit: 'contain',
            userSelect: 'none',
            ...(imgFilter ? { filter: imgFilter } : {}),
          }}
        />
      )}
    </span>
  );
}
export const Settings = ms('settings');
export const Share = ms('share');
export const ShoppingBag = ms('shopping_bag');
export const ShoppingCart = ms('shopping_cart');
export const Sparkles = ms('auto_awesome');
export const Square = ms('stop');
export const Sun = ms('light_mode');
export const Moon = ms('dark_mode');
export const ThumbsDown = ms('thumb_down');
export const ThumbsUp = ms('thumb_up');
export const Trash2 = ms('delete');
export const Waves = ms('waves');
export const Wrench = ms('build');
export const Warning = ms('warning');
export const X = ms('close');
export const Zap = ms('bolt');
